import React, {useEffect, useRef, useState} from 'react';
import {InstalledBaseNode, InstalledBaseNodeType} from "../../../codegen/graphql";
import {BasicModalContent, LoadingScreen} from "@dreebit-registry/vsm-microservice-components";
import Box from "@mui/material/Box";
import {Typography} from "@mui/joy";
import {useTranslation} from "react-i18next";
import {KeyboardArrowDownRounded, KeyboardArrowUpRounded} from "@mui-symbols-material/w300";
import NodeTypeSelectionItem from "./nodeTypeSelectionItem";
import NodeModalTitle from "../NodeModalTitle";
import InstalledBaseNodePathBreadcrumb from "../../InstalledBaseNodePathBreadcrumb";
import {canAddChildType, childRuleHasRoom, getChildRule} from "../../../utils/nodeChildRules";
import {useAllowedChildNodeTypes} from "../../../hooks/useAllowedChildNodeTypes";

interface Props {
    node: InstalledBaseNode;
    onNodeTypeSelect?: (nodeType: InstalledBaseNodeType) => void;
    onClose?: () => void;
    /** When true and exactly one type is recommended, auto-select it (skip this screen). */
    allowAutoSelect?: boolean;
}

const NodeTypeGrid = ({nodeTypes, onNodeTypeSelect, getItemRuleProps}: {
    nodeTypes: InstalledBaseNodeType[];
    onNodeTypeSelect?: (nodeType: InstalledBaseNodeType) => void;
    getItemRuleProps?: (nodeType: InstalledBaseNodeType) => {disabled?: boolean; disabledHint?: string};
}) => (
    <Box display={'grid'} gridTemplateColumns={{xs: '1fr', sm: '1fr 1fr'}} gap={'13px'}>
        {nodeTypes.map((nodeType) => (
            <NodeTypeSelectionItem
                nodeType={nodeType}
                onNodeTypeSelect={onNodeTypeSelect}
                key={nodeType.id}
                {...getItemRuleProps?.(nodeType)}
            />
        ))}
    </Box>
);

const NodeTypeSelect = ({onNodeTypeSelect, onClose, node, allowAutoSelect}: Props) => {
    const {t} = useTranslation();

    const [showAllManual, setShowAllManual] = useState(false);

    // Structural whitelist (IBM-462) is applied by the hook: types that can never be
    // created under this parent are hidden entirely; whitelisted types at their count
    // limit stay visible but disabled (see getItemRuleProps below).
    const nodeRules = node.nodeRules;
    const {
        recommended: recommendedNodeTypes,
        other: otherNodeTypes,
        whitelisted,
        loading,
        error,
    } = useAllowedChildNodeTypes(node);

    const getItemRuleProps = (nodeType: InstalledBaseNodeType) => {
        const rule = getChildRule(nodeRules, nodeType.key);
        if (!rule || childRuleHasRoom(rule)) return {};
        return {
            disabled: true,
            disabledHint: t('Limit reached ({{current}}/{{max}})', {
                current: rule.currentNodeCount,
                max: rule.limits!.max,
            }),
        };
    };

    const hasRecommended = recommendedNodeTypes.length > 0;
    const moreCount = otherNodeTypes.length;
    const showAll = showAllManual || !hasRecommended;
    // Nothing behind the collapse means nothing to collapse: when every allowed type is
    // already recommended, the toggle would just expand into an empty grid.
    const showToggle = hasRecommended && moreCount > 0;

    // Skip this screen entirely when there's a single obvious choice. Guarded so it fires
    // once per mount; the parent suppresses it (allowAutoSelect=false) after a back/edit.
    const didAutoSelect = useRef(false);
    useEffect(() => {
        if (
            allowAutoSelect && !didAutoSelect.current && recommendedNodeTypes.length === 1 &&
            canAddChildType(nodeRules, recommendedNodeTypes[0].key)
        ) {
            didAutoSelect.current = true;
            onNodeTypeSelect?.(recommendedNodeTypes[0]);
        }
    }, [allowAutoSelect, recommendedNodeTypes, onNodeTypeSelect, nodeRules]);

    // After a back/edit from the auto-skipped form (single recommendation), the user is
    // looking for a different type — expand the full list for them once.
    const didAutoExpand = useRef(false);
    useEffect(() => {
        if (!allowAutoSelect && !didAutoExpand.current && recommendedNodeTypes.length === 1) {
            didAutoExpand.current = true;
            setShowAllManual(true);
        }
    }, [allowAutoSelect, recommendedNodeTypes]);

    return (
        <BasicModalContent
            onClose={onClose}
            style={{gap: '13px'}}
            title={<NodeModalTitle prefix={t('Create node under')} node={node} labelType={'title'}/>}
        >
            {/* mt cancels the BasicModalContent gap so the band sits flush, like the asset form */}
            <Box sx={{mt: 0.5, mb: 1}}>
                <InstalledBaseNodePathBreadcrumb nodeId={node.id} />
            </Box>

            {/* Without these the dialog body is simply blank whenever the derived list is
                empty — in flight, failed, or a parent whose childRules reference types the
                industry-filtered catalogue doesn't contain. */}
            {loading && <LoadingScreen size={'sm'}/>}
            {!loading && !whitelisted.length && (
                <Typography level={'body'} textColor={'text.secondary'}>
                    {error
                        ? t('The node types could not be loaded. Please close the dialog and try again.')
                        : t('No node types can be created under this node.')}
                </Typography>
            )}

            {hasRecommended && (
                <Box display={'flex'} flexDirection={'column'} gap={'8px'}>
                    <Typography level={'headline'}>{t('Recommended')}</Typography>
                    <NodeTypeGrid nodeTypes={recommendedNodeTypes} onNodeTypeSelect={onNodeTypeSelect} getItemRuleProps={getItemRuleProps}/>
                </Box>
            )}

            {showToggle && (
                <Box
                    onClick={() => setShowAllManual((prev) => !prev)}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        cursor: 'pointer',
                    }}
                >
                    <Typography level={'headline'} sx={{flexGrow: 1}}>
                        {t('Show all node types')}
                    </Typography>
                    {!showAll && (
                        <Typography level={'body-sm'} textColor={'text.secondary'}>
                            {t('{{count}} more', {count: moreCount})}
                        </Typography>
                    )}
                    {showAll ? <KeyboardArrowUpRounded/> : <KeyboardArrowDownRounded/>}
                </Box>
            )}

            {showAll && moreCount > 0 && (
                <NodeTypeGrid nodeTypes={otherNodeTypes} onNodeTypeSelect={onNodeTypeSelect} getItemRuleProps={getItemRuleProps}/>
            )}
        </BasicModalContent>
    )
};

export default NodeTypeSelect;
